import { FilteractionObject } from '@yes.technology/react-toolkit'
import useObjectClassFilteractions from 'filteraction/shared/hooks/useObjectClassFilteractions'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18n-lite'
import { AllowedLocales } from 'shared/utils/date/formatDate'
import { useSituations } from 'situation/shared'
import { useFilterFieldsContext } from '../../../context'
import type { GroupedFieldValues } from '../../../types'
import type { FilteractionSelectionProps } from '../../FilteractionSelection/FilteractionSelection.types'
import getOnSelectFilteraction from '../../FilteractionSelection/getOnSelectFilteraction'
import getFilterStateFromFilteraction from '../../FilteractionSelection/getQueryDataFromFilteraction'

export type UseSectionFilteractionParams = {
  setGroupPosition: (position: number) => void
}

export type UseSectionFilteractionResult = {
  filteractionValue: string
  setFilteractionValue: (value: string) => void
  changedKeysData: GroupedFieldValues | undefined
  setChangedKeysData: (value: GroupedFieldValues | undefined) => void
  filteractions: ReturnType<typeof useObjectClassFilteractions>
  onSelectFilteraction: FilteractionSelectionProps['onSelectFilteraction']
  shouldHideFilters: boolean
}

const useSectionFilteraction = ({
  setGroupPosition
}: UseSectionFilteractionParams): UseSectionFilteractionResult => {
  const { language } = useTranslation()
  const locale = language as AllowedLocales

  const { config, form } = useFilterFieldsContext()

  const {
    uuidCurrentSitemodelObjectclass,
    filterAreaLayout,
    filteractionOptions,
    dependsOnFilteractionOptions
  } = config

  const {
    sections,
    handleChange,
    handleClear,
    handleComposition,
    executeFilter,
    defaultExecutedFilteraction,
    initialGroupedFieldValues
  } = form

  const initialFieldValues = initialGroupedFieldValues[1]

  const [filteractionValue, setFilteractionValue] = useState('')
  const [changedKeysData, setChangedKeysData] = useState<GroupedFieldValues>()
  const executedDefaultFilteractionRef = useRef<string | undefined>(undefined)

  const filteractions = useObjectClassFilteractions({
    objectclassUuid: uuidCurrentSitemodelObjectclass,
    filteractionOptions,
    dependsOnFilteractionOptions
  })

  const situations = useSituations()

  const onSelectFilteraction = getOnSelectFilteraction({
    initialFieldValues,
    sections,
    situations,
    handleChange,
    handleClear,
    handleComposition,
    setGroupPosition,
    setChangedKeysData,
    locale
  })

  const executeDefaultFilteraction = useCallback(
    (filteraction: FilteractionObject) => {
      const { queryData, composition: comp } = getFilterStateFromFilteraction({
        initialFieldValues,
        sections,
        situations,
        filteraction,
        locale
      })

      executeFilter(queryData, comp)
      setFilteractionValue(filteraction.des)
      handleChange(queryData)
      handleComposition(comp)
    },
    [
      initialFieldValues,
      sections,
      situations,
      locale,
      executeFilter,
      handleChange,
      handleComposition
    ]
  )

  useEffect(() => {
    if (!defaultExecutedFilteraction) {
      executedDefaultFilteractionRef.current = undefined
      return
    }

    if (
      executedDefaultFilteractionRef.current === defaultExecutedFilteraction
    ) {
      return
    }

    if (filteractions.length === 0 || situations.length === 0) {
      return
    }

    const filteraction = filteractions.find(
      (fa) => fa.uuid === defaultExecutedFilteraction
    )

    if (filteraction) {
      executeDefaultFilteraction(filteraction)
      executedDefaultFilteractionRef.current = defaultExecutedFilteraction
    }
  }, [
    defaultExecutedFilteraction,
    filteractions,
    situations,
    executeDefaultFilteraction
  ])

  const hideFiltersAfterFilteraction =
    !!filterAreaLayout?.filteractions?.hide_filters_after_filteraction

  const shouldHideFilters = hideFiltersAfterFilteraction && !!filteractionValue

  return {
    filteractionValue,
    setFilteractionValue,
    changedKeysData,
    setChangedKeysData,
    filteractions,
    onSelectFilteraction,
    shouldHideFilters
  }
}

export default useSectionFilteraction
